/**
 * Utility functions for handling session storage and URL parameter decoding
 */

/**
 * The name of the URL parameter that contains the base64-encoded JSON data
 */
export const ENCODED_DATA_PARAM = 'token';

/**
 * Interface for the decoded JSON data structure
 */
export interface EncodedDataParams {
  AgencyContactPerson?: string;
  AgentName?: string;
  AgencyEmail?: string;
  [key: string]: any;
}

/**
 * Mapping of JSON keys to session storage keys
 */
export const STORAGE_KEY_MAPPINGS: Record<string, string> = {
  AgencyContactPerson: 'agentPersonInCharge',
  AgentName: 'agentName',
  AgencyEmail: 'agencyEmail',
};

/**
 * Decodes a base64 encoded string
 * @param str Base64 encoded string
 * @returns Decoded string or empty string if invalid
 */
export const decodeBase64 = (str: string): string => {
  try {
    return decodeURIComponent(atob(str));
  } catch (e) {
    console.error('Error decoding base64 string:', e);
    return '';
  }
};

/**
 * Extracts and parses the base64-encoded JSON object from the URL
 * @returns Object with decoded parameters
 */
export const extractUrlParams = (): EncodedDataParams => {
  const queryString = window.location.search;
  if (!queryString) return {};
  
  // Get URL parameters
  const urlParams = new URLSearchParams(queryString);
  const encodedData = urlParams.get(ENCODED_DATA_PARAM);
  
  if (!encodedData) return {};
  
  try {
    // Decode the base64 string to JSON
    const decodedString = decodeBase64(encodedData);
    
    // Parse the JSON string
    const jsonData = JSON.parse(decodedString);
    
    return jsonData;
  } catch (error) {
    console.error('Error parsing encoded JSON data:', error);
    return {};
  }
};

/**
 * Saves URL parameters to session storage
 * @param params Parameters to save
 */
export const saveParamsToSession = (params: EncodedDataParams): void => {
  try {
    // Map JSON data to session storage keys
    Object.entries(STORAGE_KEY_MAPPINGS).forEach(([jsonKey, storageKey]) => {
      if (params[jsonKey]) {
        sessionStorage.setItem(storageKey, params[jsonKey]);
      }
    });
    
    // Set a flag indicating params were processed
    localStorage.setItem('paramsProcessed', 'true');
  } catch (e) {
    console.error('Error saving to localStorage:', e);
  }
};

/**
 * Processes URL parameters and saves to session storage if not already processed
 * @returns True if parameters were processed, false if already processed or none found
 */
export const processUrlParams = (): boolean => {  
  const params = extractUrlParams();
  
  // Check if any parameters were found
  if (Object.keys(params).length > 0) {
    saveParamsToSession(params);
    return true;
  }
  
  return false;
};
